import React, { useContext, useEffect, useMemo, useState } from "react";
import { Modal, Button, Form, Row, Col, Image, Badge } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import {
  getAllRewardsThunk,
  sendVerificationCodeThunk,
  verifyCodeThunk,
} from "../store/rewards";
import Context from "../Context";
import AddRewardItemModal from "./AddRewardItemModal";

const PLACEHOLDER_IMAGE =
  "https://theme-assets.getbento.com/sensei/a342b2d.sensei/assets/images/catering-item-placeholder-704x520.png";

export function RewardModal({ isOpen, onClose, points, phone, handlePhoneChange }) {
  const dispatch = useDispatch();
  const rewards = useSelector((state) => state.rewards.rewards) || [];
  const { restaurantId, changedCart, setChangedCart } = useContext(Context);
  const [selectedItem, setSelectedItem] = useState(null);
  const [addFreeItemModal, setAddFreeItemModal] = useState(false);
  const [currentRewardItem, setCurrentRewardItem] = useState(null);
  const [currentDiscountReward, setCurrentDiscountReward] = useState(null);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [verificationStep, setVerificationStep] = useState(1);
  const [verificationMessage, setVerificationMessage] = useState("");
  const [choosedReward, setChoosedReward] = useState(null);

  const sortedRewards = useMemo(() => {
    return [...rewards].sort(
      (a, b) => Number(a.need_amount) - Number(b.need_amount)
    );
  }, [rewards]);

  useEffect(() => {
    const stored = localStorage.getItem("rewardItem");
    setCurrentRewardItem(stored ? JSON.parse(stored) : null);
    const storedDiscount = localStorage.getItem(`discountReward-${restaurantId}`);
    setCurrentDiscountReward(storedDiscount ? JSON.parse(storedDiscount) : null);
  }, [changedCart, restaurantId]);

  useEffect(() => {
    if (isOpen) dispatch(getAllRewardsThunk(restaurantId));
  }, [dispatch, restaurantId, isOpen]);

  const openItemModal = (reward) => {
    if (!reward?.Item) return;
    setSelectedItem(reward.Item);
    setAddFreeItemModal(true);
  };

  const handleRedeem = async (reward) => {
    const verifiedPhone = localStorage.getItem("verifiedPhoneNumber");

    if (verifiedPhone !== phone) {
      setShowVerifyModal(true);
      setChoosedReward(reward);
      return;
    }

    if (reward.reward_type === "fixed_amount") {
      const isCurrentlySelected = currentDiscountReward?.id === reward.id;
      if (isCurrentlySelected) {
        localStorage.removeItem(`discountReward-${restaurantId}`);
        setCurrentDiscountReward(null);
      } else {
        localStorage.setItem(`discountReward-${restaurantId}`, JSON.stringify(reward));
        setCurrentDiscountReward(reward);
      }
      setChangedCart(!changedCart);
      return;
    }

    openItemModal(reward);
  };

  const handleSendCode = async () => {
    const sendResult = await sendVerificationCodeThunk(phone)(dispatch);
    if (sendResult && sendResult.message === "success") {
      setVerificationStep(2);
      setVerificationMessage("Verification code sent to your phone.");
    } else {
      setVerificationMessage("Failed to send verification code. Please try again.");
    }
  };

  const handleVerifyCode = async (userCode) => {
    const verifyResult = await verifyCodeThunk(phone, userCode)(dispatch);
    if (verifyResult && verifyResult.message === "success") {
      localStorage.setItem("verifiedPhoneNumber", phone);
      setVerificationMessage("Phone verified successfully!");
      setTimeout(() => {
        setShowVerifyModal(false);
        if (choosedReward?.reward_type === "fixed_amount") {
          localStorage.setItem(
            `discountReward-${restaurantId}`,
            JSON.stringify(choosedReward)
          );
          setCurrentDiscountReward(choosedReward);
          setChangedCart(!changedCart);
        } else {
          openItemModal(choosedReward);
        }
      }, 1000);
    } else {
      setVerificationMessage("Verification failed. Please try again.");
    }
  };

  const formatPhoneNumber = (phoneNumber) => {
    if (phoneNumber.length > 6) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6)}`;
    }
    if (phoneNumber.length > 3) return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    if (phoneNumber.length > 0) return `(${phoneNumber}`;
    return "";
  };

  return (
    <>
      <Modal show={isOpen && !showVerifyModal} onHide={onClose} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title className="w-100 text-center">
            Your Points: <Badge bg="success">{points}</Badge>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: "600px", overflowY: "auto" }}>
          <Form.Group className="mb-3 text-center">
            <Form.Control
              type="tel"
              className="text-center"
              value={formatPhoneNumber(phone)}
              onChange={handlePhoneChange}
              placeholder="(123) 456-7890"
              required
              maxLength={14}
            />
            <Form.Text className="text-danger">
              Only one reward can be redeemed per order.
            </Form.Text>
          </Form.Group>

          {sortedRewards.length === 0 ? (
            <p className="text-center text-muted">No rewards available currently.</p>
          ) : (
            sortedRewards.map((reward) => {
              const isFreeItem = reward.reward_type === "free_item" && reward.Item;
              const isDiscountOff = reward.reward_type === "fixed_amount";
              const isSelected = isFreeItem
                ? currentRewardItem?.Item?.id === reward.Item?.id
                : currentDiscountReward?.id === reward.id;
              const needed = Number(reward.need_amount) || 0;
              const isLocked = Number(points) < needed;
              const discountOffLabel =
                isDiscountOff && reward.discount_amount != null
                  ? `$${Number(reward.discount_amount).toFixed(2)} off`
                  : null;
              const title = isDiscountOff
                ? discountOffLabel || "Discount reward"
                : reward.Item?.name || "Reward";

              return (
                <Row
                  key={reward.id}
                  className="mb-3 border rounded p-2 align-items-center"
                >
                  <Col xs={12} md={3}>
                    {isFreeItem ? (
                      <Image
                        src={reward.Item.image_url || PLACEHOLDER_IMAGE}
                        fluid
                        rounded
                      />
                    ) : (
                      <div
                        className="d-flex align-items-center justify-content-center bg-light rounded"
                        style={{ height: 100 }}
                      >
                        <span className="fw-bold text-warning">$ Off</span>
                      </div>
                    )}
                  </Col>
                  <Col xs={12} md={6}>
                    <h5>{title}</h5>
                    {isFreeItem && (
                      <p className="mb-1">
                        {reward.Item.description && reward.Item.description !== "null"
                          ? reward.Item.description
                          : "No description available."}
                      </p>
                    )}
                    <p className="mb-0">
                      <strong>Points Needed:</strong> {points}/{reward.need_amount}
                    </p>
                    {isLocked && (
                      <small className="text-danger">
                        Need {needed - Number(points)} more points
                      </small>
                    )}
                  </Col>
                  <Col xs={12} md={3} className="text-md-end text-center">
                    <Button
                      size="sm"
                      variant={isSelected ? "secondary" : "primary"}
                      onClick={() => handleRedeem(reward)}
                      disabled={isLocked || isSelected}
                    >
                      {isSelected ? "Selected Reward" : "Redeem"}
                    </Button>
                  </Col>
                </Row>
              );
            })
          )}
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-between">
          <div>
            {currentRewardItem?.Item
              ? `Current Reward: ${currentRewardItem.Item.name}`
              : currentDiscountReward
              ? `Current Discount: $${Number(currentDiscountReward.discount_amount || 0).toFixed(2)} off`
              : "No reward selected"}
          </div>
          <Button variant="success" onClick={onClose}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showVerifyModal} onHide={() => setShowVerifyModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Phone Verification</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {verificationStep === 1 && (
            <>
              <p className="mb-3">We need to verify your phone number to redeem points:</p>
              <Button variant="primary" onClick={handleSendCode}>
                Send Code
              </Button>
            </>
          )}
          {verificationStep === 2 && (
            <>
              <p className="mb-3">Enter the code sent to your phone:</p>
              <Form.Control
                type="tel"
                className="text-center mx-auto"
                placeholder="Enter code"
                maxLength={6}
                style={{ maxWidth: "200px" }}
                onChange={(e) => {
                  const code = e.target.value;
                  if (code.length === 6) handleVerifyCode(code);
                }}
              />
            </>
          )}
          {verificationMessage && (
            <p className="mt-3 text-success">{verificationMessage}</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowVerifyModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <AddRewardItemModal
        modal={addFreeItemModal}
        setModal={() => setAddFreeItemModal(false)}
        item={selectedItem}
      />
    </>
  );
}
